/**
 * B1-#9: Global keyboard shortcuts for DataSentinel.
 *
 * Shortcuts are only fired when no input/textarea/select/contenteditable is focused.
 * The overlay is toggled with "?" (Shift+/).
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export interface Shortcut {
  key: string          // display key (e.g. "D", "?")
  description: string
  path?: string        // if set, navigate there
  action?: () => void  // alternative: run a custom action
}

export const SHORTCUTS: Shortcut[] = [
  { key: 'D', description: 'Go to Dashboard',        path: '/dashboard' },
  { key: 'P', description: 'Go to Projects',          path: '/projects' },
  { key: 'U', description: 'Go to Datasets',          path: '/datasets' },
  { key: 'R', description: 'Run DQA',                 path: '/run' },
  { key: 'V', description: 'View Violations',         path: '/violations' },
  { key: 'C', description: 'Correction Workbench',    path: '/corrections' },
  { key: 'T', description: 'Trends',                  path: '/trends' },
  { key: 'S', description: 'Schedules',               path: '/schedules' },
  { key: 'A', description: 'Audit Trail',             path: '/audit' },
  { key: 'H', description: 'AI Chat',                 path: '/ai-chat' },
  { key: '?', description: 'Show keyboard shortcuts', path: undefined },
]

function isInputFocused(): boolean {
  const tag = (document.activeElement?.tagName || '').toLowerCase()
  const isEditable = (document.activeElement as HTMLElement)?.isContentEditable
  return tag === 'input' || tag === 'textarea' || tag === 'select' || isEditable
}

export function useKeyboardShortcuts(onShowHelp: () => void) {
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore shortcuts when modifier keys are held (cmd/ctrl/alt)
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Ignore when an input is focused
      if (isInputFocused()) return

      const key = e.key.toUpperCase()

      // "?" = Shift+/ or just Shift+?
      if (e.key === '?') {
        e.preventDefault()
        onShowHelp()
        return
      }

      const match = SHORTCUTS.find(s => s.key === key && s.path !== undefined)
      if (match?.path) {
        e.preventDefault()
        navigate(match.path)
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate, onShowHelp])
}
